import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, SearchField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccessNotice } from '../components';
import { HistoryDrawer } from '../history';
import type { OperationsAccess } from '../operationsAccess';
import { OperationTable, PreviewModal } from './components';
import {
    STATUS_FILTERS,
    type OperationRow,
    type StatusFilter,
    type TemplatesEmptyState,
} from './templatesMeta';

type TemplatesPageViewProps = {
    access: OperationsAccess;
    canRead: boolean;
    locale: LocaleCode;
    /**
     * The catalog is still in flight. The toolbar keeps its real labels and is
     * disabled; only the rows skeleton.
     */
    isLoading: boolean;
    rows: OperationRow[];
    totalCount: number;
    visibleCount: number;
    search: string;
    onSearchChange: (value: string) => void;
    statusFilter: StatusFilter;
    onStatusFilterChange: (filter: StatusFilter) => void;
    emptyState: TemplatesEmptyState;
    onEdit: (key: string) => void;
    /** The row whose recipe is on screen, or `null` when the modal is closed. */
    previewRow: OperationRow | null;
    onPreview: (key: string) => void;
    onPreviewOpenChange: (open: boolean) => void;
    /** Whether this session may open the editor — decides the modal's Edit button. */
    canEdit: boolean;
    onOpenHistory: (key: string) => void;
    /** The template whose history drawer is open, `null` when it is closed. */
    historyKey: string | null;
    onCloseHistory: () => void;
    /** `null` for a session that may not change anything. */
    onSetBlocked: ((key: string, blocked: boolean) => void) | null;
    /** `null` for a session that may not change anything — same rule as `onSetBlocked`. */
    onCreate: (() => void) | null;
    /**
     * Open the category management screen. Offered whatever the role, unlike
     * `onCreate` — it is navigation to a screen that renders itself read-only,
     * not a mutation, and someone who can see the chips can see where they come
     * from.
     */
    onManageCategories: () => void;
};

/**
 * Templates tab — render only. Toolbar (search, status filter, count), the
 * catalog table, and two footer notes.
 *
 * The footer notes are not decoration and neither is negotiable:
 *
 * - **What editing does.** "Editing publishes a new version; entries keep the
 *   version they posted under; operations are blocked, never deleted." Someone
 *   about to change how an expense posts has to know it will not rewrite last
 *   March — that is the single most surprising thing about this screen and the
 *   only place it is stated.
 * - **What the status filter leaves out.** Every filter but *All* hides the
 *   system rows, because a code-defined flow has no status to match. Said out
 *   loud, that is a rule; unsaid, it is nine rows vanishing for no visible
 *   reason.
 */
export const TemplatesPageView: React.FC<TemplatesPageViewProps> = ({
    access,
    canRead,
    locale,
    isLoading,
    rows,
    totalCount,
    visibleCount,
    search,
    onSearchChange,
    statusFilter,
    onStatusFilterChange,
    emptyState,
    onEdit,
    previewRow,
    onPreview,
    onPreviewOpenChange,
    canEdit,
    onOpenHistory,
    historyKey,
    onCloseHistory,
    onSetBlocked,
    onCreate,
    onManageCategories,
}) => {
    const { t } = useTranslation('pages');

    if (!canRead) {
        return (
            <section
                aria-label={t('operations.templates.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('operations-templates-section')}
            >
                <AccessNotice
                    access={
                        access as Exclude<OperationsAccess, { kind: 'full' }>
                    }
                />
            </section>
        );
    }

    return (
        <section
            aria-label={t('operations.templates.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('operations-templates-section')}
        >
            <div
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('operations-toolbar')}
            >
                <SearchField
                    aria-label={t('operations.searchAria')}
                    className="w-full sm:max-w-xs"
                    isDisabled={isLoading}
                    onChange={onSearchChange}
                    value={search}
                    {...getNodeTestIdProp('operations-search')}
                >
                    <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input
                            placeholder={t('operations.searchPlaceholder')}
                        />
                        <SearchField.ClearButton />
                    </SearchField.Group>
                </SearchField>

                <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                    <Segment
                        aria-label={t('operations.filters.statusAria')}
                        isDisabled={isLoading}
                        onSelectionChange={(key) =>
                            onStatusFilterChange(key as StatusFilter)
                        }
                        selectedKey={statusFilter}
                        size="sm"
                        {...getNodeTestIdProp('operations-status-filter')}
                    >
                        {STATUS_FILTERS.map((filter) => (
                            <Segment.Item
                                id={filter.id}
                                key={filter.id}
                                {...getNodeTestIdProp(
                                    `operations-filter-${filter.id}`,
                                )}
                            >
                                <Segment.Separator />
                                {t(filter.labelKey)}
                            </Segment.Item>
                        ))}
                    </Segment>

                    <span
                        className="whitespace-nowrap text-sm text-muted tabular-nums"
                        {...getNodeTestIdProp('operations-count')}
                    >
                        {isLoading
                            ? t('operations.countPending')
                            : t('operations.count', {
                                  visible: visibleCount,
                                  total: totalCount,
                              })}
                    </span>

                    <Button
                        isDisabled={isLoading}
                        onPress={onManageCategories}
                        size="sm"
                        variant="outline"
                        {...getNodeTestIdProp('operations-manage-categories')}
                    >
                        {t('operations.manageCategories')}
                    </Button>

                    {onCreate ? (
                        <Button
                            isDisabled={isLoading}
                            onPress={onCreate}
                            size="sm"
                            {...getNodeTestIdProp('operations-add')}
                        >
                            <Icon aria-hidden icon="gravity-ui:plus" />
                            {t('operations.addOperation')}
                        </Button>
                    ) : null}
                </div>
            </div>

            <OperationTable
                ariaLabel={t('operations.tableAria')}
                emptyState={emptyState}
                isLoading={isLoading}
                locale={locale}
                onEdit={onEdit}
                onOpenHistory={onOpenHistory}
                onPreview={onPreview}
                onSetBlocked={onSetBlocked}
                rows={rows}
            />

            <PreviewModal
                canEdit={canEdit}
                locale={locale}
                onEdit={onEdit}
                onOpenChange={onPreviewOpenChange}
                operation={previewRow}
            />

            {/* Over the catalog rather than away from it: a reader asking why an
                operation posts the way it does is asking about the row in front
                of them, and a navigation used to cost them their search and
                filter on the way back. */}
            <HistoryDrawer
                onClose={onCloseHistory}
                onOpenEditor={onEdit}
                operationKey={historyKey}
            />

            <div className="flex flex-col gap-1">
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operations-versioning-note')}
                >
                    {t('operations.footer.versioning')}
                </p>
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operations-system-note')}
                >
                    {t('operations.footer.systemRows')}
                </p>
                {access.kind === 'readOnly' ? (
                    <p
                        className="text-xs text-muted"
                        {...getNodeTestIdProp('operations-read-only-note')}
                    >
                        {t('operations.footer.readOnly')}
                    </p>
                ) : null}
            </div>
        </section>
    );
};
